import React from "react";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const GREEN = "#17BD3B";

const stats = [
  { value: "50K+", label: "Tickets sold" },
  { value: "₹2Cr+", label: "Revenue processed" },
  { value: "48 hrs", label: "Payout settlement" },
];

const marqueeTop = [
  "Waitlist Management",
  "Brand Partnerships",
  "Repeat Attendance Rewards",
  "Email Campaigns",
  "Real-time Sales Tracking",
  "0% Commission",
  "Fast Payouts",
];

const marqueeBottom = [
  "Ticket QR Codes",
  "Event Page Builder",
  "Group Bookings",
  "Referral Programs",
  "Transparent Breakdowns",
  "Instant Listing",
  "48-hr Settlement",
  "Custom Branding",
];

const features = [
  {
    icon: "bolt",
    title: "Instant listing, 0% commission",
    desc: "Go live in under 10 minutes. Create multiple ticket tiers, add discount codes, set capacity limits, and manage a waitlist — all from one dashboard.",
    points: ["Multiple ticket tiers", "Promo & discount codes", "Waitlist controls", "Real-time analytics"],
  },
  {
    icon: "user",
    title: "Build your Host Profile",
    desc: "Your events, your brand. Create a dedicated host page that showcases your past events, upcoming shows, and social proof — all linkable from your bio.",
    points: ["Custom host page URL", "Past & upcoming events", "Attendee testimonials", "Link-in-bio ready"],
  },
  {
    icon: "gift",
    title: "Reward your attendees",
    desc: "Keep your audience coming back. Offer show-up rewards, exclusive discounts for repeat attendees, and referral incentives that drive organic growth.",
    points: ["Show-up reward points", "Repeat-attendee perks", "Referral programs", "Exclusive discount codes"],
  },
  {
    icon: "mail",
    title: "Automated CRM built in",
    desc: "Reach your attendees where they are — WhatsApp, email, or both. Send confirmations, reminders, and post-event follow-ups without lifting a finger.",
    points: ["WhatsApp & email blasts", "RSVP confirmations", "Smart day-of reminders", "Post-event feedback"],
  },
  {
    icon: "card",
    title: "Fast, transparent payouts",
    desc: "Get paid within 48 hours of your event. Full settlement breakdown so you always know exactly what you earned and what was deducted.",
    points: ["48-hr payout settlement", "Transparent fee breakdown", "Multiple payout methods", "GST-compliant invoices"],
  },
  {
    icon: "handshake",
    title: "Brand & sponsor partnerships",
    desc: "Match with brands that want to sponsor IRL events. Fusshn surfaces your events to relevant sponsors and handles the sponsorship workflow end-to-end.",
    points: ["Brand matching engine", "Sponsorship proposals", "Deliverables tracking", "Sponsor payouts"],
  },
];

const steps = [
  {
    title: "Create your event",
    desc: "Sign up, set up your host profile, and publish your event in under 10 minutes. Add ticket tiers, images, and a detailed description.",
  },
  {
    title: "Sell & manage tickets",
    desc: "Share your event link or embed it anywhere. Use built-in CRM tools to send reminders, manage RSVPs, and communicate with attendees.",
  },
  {
    title: "Host & get paid",
    desc: "Scan QR codes at the door with the Fusshn app. Get your payout within 48 hours of your event with a full transparent breakdown.",
  },
];

const testimonials = [
  {
    quote: "Fusshn is hands-down the easiest platform I've used to list and sell tickets. Got my first 200 bookings within 48 hours of going live.",
    name: "Ananya Rao",
    role: "Music event curator, Mumbai",
    color: "#7C3AED",
  },
  {
    quote: "The 0% commission model is a game changer. I've hosted 12 events on Fusshn and every single payout has been on time. That trust is everything.",
    name: "Rohit Mehta",
    role: "Event organiser, Bengaluru",
    color: "#0EA5B7",
  },
  {
    quote: "The WhatsApp blast feature alone saved me hours. My attendees get reminded automatically and my show-up rate went from 70% to 92%.",
    name: "Sneha Kapoor",
    role: "Community lead, Delhi",
    color: "#22C55E",
  },
  {
    quote: "Finally a platform that feels like it was built for organisers, not just attendees. The analytics dashboard gives me everything I need post-event.",
    name: "Arjun Nair",
    role: "Festival producer, Pune",
    color: "#F59E0B",
  },
];

const finalPills = ["List instantly", "Build your profile", "Reward attendees", "Sell more", "Get paid faster"];

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

const FeatureIcon = ({ name }) => {
  const common = {
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: GREEN,
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  switch (name) {
    case "bolt":
      return (
        <svg {...common}>
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
      );
    case "user":
      return (
        <svg {...common}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case "gift":
      return (
        <svg {...common}>
          <rect x="3" y="8" width="18" height="4" rx="1" />
          <path d="M12 8v13M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
          <path d="M12 8S10.5 3 8 3a2.5 2.5 0 0 0 0 5h4zM12 8s1.5-5 4-5a2.5 2.5 0 0 1 0 5h-4z" />
        </svg>
      );
    case "mail":
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
        </svg>
      );
    case "card":
      return (
        <svg {...common}>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <path d="M2 10h20" />
        </svg>
      );
    case "handshake":
      return (
        <svg {...common}>
          <path d="M11 17l2 2a1 1 0 0 0 1.5 0l3-3" />
          <path d="M13 13l3 3 4-4-5-5-2 1H8L3 9l4 4" />
          <path d="M7 13l3 3" />
        </svg>
      );
    default:
      return null;
  }
};

const ArrowRight = ({ className = "" }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const Star = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={GREEN} stroke="none">
    <path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.7 7L12 17.8 5.7 21.5l1.7-7L2 9.8l7.1-.6L12 2z" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Small components                                                   */
/* ------------------------------------------------------------------ */

const MarqueeRow = ({ items, reverse }) => {
  const doubled = [...items, ...items];
  return (
    <div className="flex overflow-hidden marquee-pause">
      <div className={`flex shrink-0 gap-4 pr-4 ${reverse ? "animate-marquee-reverse" : "animate-marquee"}`}>
        {doubled.map((item, i) => (
          <div
            key={i}
            className="flex items-center gap-2.5 whitespace-nowrap rounded-full border border-[#262626] bg-[#0E0E0E] px-6 py-3.5 text-[15px] font-medium text-gray-200"
          >
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: GREEN }} />
            {item}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const SetupAccount = () => {
  return (
    <div className="bg-[#050505] text-white font-poppins overflow-x-hidden">
      {/* ============================= HERO ============================= */}
      <section
        className="relative px-4 sm:px-6 lg:px-8 pt-28 sm:pt-36 pb-20"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, rgba(23,189,59,0.18) 0%, rgba(23,189,59,0.04) 35%, rgba(5,5,5,0) 60%), #050505",
        }}
      >
        <div className="mx-auto max-w-[1100px] text-center">
          {/* badge */}
          <div
            className="mx-auto mb-10 inline-flex items-center gap-2.5 rounded-full border px-5 py-2.5 text-sm font-medium"
            style={{ borderColor: "rgba(23,189,59,0.4)", background: "rgba(23,189,59,0.08)", color: "#8EE3A2" }}
          >
            <span className="h-2 w-2 rounded-full" style={{ background: GREEN, boxShadow: `0 0 8px ${GREEN}` }} />
            Now live across India &mdash; Mumbai, Delhi, Bengaluru &amp; more
          </div>

          {/* headline */}
          <h1 className="font-display mx-auto max-w-[950px] text-[44px] leading-[1.04] sm:text-[68px] md:text-[84px]">
            The platform built <span style={{ color: GREEN }}>for organisers</span> who mean business.
          </h1>

          {/* subtext */}
          <p className="mx-auto mt-8 max-w-[640px] text-[17px] leading-relaxed text-gray-400 sm:text-xl">
            List your events, sell tickets, manage attendees, and get paid &mdash; all on Fusshn. Zero platform
            commission. No gatekeeping.
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="https://organiser.fusshn.com"
              className="group inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 text-base font-semibold text-white no-underline transition-transform duration-300 hover:scale-[1.03]"
              style={{ background: GREEN, boxShadow: "0 12px 30px rgba(23,189,59,0.35)" }}
            >
              Start listing for free
              <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link
              to="/Contact_Us"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#2A2A2A] bg-[#0E0E0E] px-8 py-4 text-base font-semibold text-white no-underline transition-colors duration-300 hover:border-[#444]"
            >
              Talk to our team
            </Link>
          </div>

          {/* trust line */}
          <p className="mt-8 text-sm text-gray-500">
            Trusted by <span className="font-semibold text-gray-300">1,200+ organisers</span> across India &middot; Payouts
            in 48 hrs &middot; 0% commission
          </p>

          {/* stats */}
          <div className="mx-auto mt-16 grid max-w-[760px] grid-cols-1 gap-10 sm:grid-cols-3">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-display text-[40px] leading-none sm:text-[48px]" style={{ color: "#fff" }}>
                  {s.value}
                </div>
                <div className="mt-3 text-sm text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================= MARQUEE ============================= */}
      <section className="space-y-4 py-10">
        <MarqueeRow items={marqueeTop} />
        <MarqueeRow items={marqueeBottom} reverse />
      </section>

      {/* ============================= FEATURES ============================= */}
      <section className="px-4 sm:px-6 lg:px-8 py-20">
        <div className="mx-auto max-w-[1200px]">
          <p className="text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: GREEN }}>
            Platform Features
          </p>
          <h2 className="font-display mt-4 max-w-[680px] text-[38px] leading-[1.05] sm:text-[52px]">
            Everything you need to host and grow.
          </h2>
          <p className="mt-6 max-w-[560px] text-lg text-gray-400">
            From first listing to full-scale events &mdash; Fusshn gives organisers every tool, with none of the
            complexity.
          </p>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="group relative overflow-hidden rounded-2xl border border-[#1C1C1C] bg-[#0C0C0C] p-7 transition-colors duration-300 hover:border-[#17BD3B]/40"
              >
                {/* number watermark */}
                <span className="font-display pointer-events-none absolute right-5 top-5 text-4xl text-white/5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {/* icon */}
                <div
                  className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl"
                  style={{ background: "rgba(23,189,59,0.12)" }}
                >
                  <FeatureIcon name={f.icon} />
                </div>
                <h3 className="text-xl font-semibold text-white">{f.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-gray-400">{f.desc}</p>
                <ul className="mt-6 space-y-2.5">
                  {f.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-[15px] text-gray-300">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: GREEN }} />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================= HOW IT WORKS ============================= */}
      <section className="px-4 sm:px-6 lg:px-8 py-20">
        <div className="mx-auto max-w-[1200px]">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: GREEN }}>
              How it works
            </p>
            <h2 className="font-display mx-auto mt-4 max-w-[800px] text-[38px] leading-[1.05] sm:text-[54px]">
              Three steps to your first sold-out event.
            </h2>
          </div>

          <div className="mt-16 grid grid-cols-1 items-stretch gap-6 md:grid-cols-3">
            {steps.map((s, i) => (
              <React.Fragment key={s.title}>
                <div className="relative rounded-2xl border border-[#1C1C1C] bg-[#0A0A0A] p-8">
                  <div className="font-display text-6xl" style={{ color: "rgba(23,189,59,0.25)" }}>
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-6 text-2xl font-semibold text-white">{s.title}</h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-gray-400">{s.desc}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ============================= TESTIMONIALS ============================= */}
      <section className="px-4 sm:px-6 lg:px-8 py-20">
        <div className="mx-auto max-w-[1200px]">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: GREEN }}>
              Organiser Stories
            </p>
            <h2 className="font-display mx-auto mt-4 max-w-[760px] text-[38px] leading-[1.05] sm:text-[54px]">
              Heard it from the people who host.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((t) => (
              <div key={t.name} className="flex flex-col rounded-2xl border border-[#1C1C1C] bg-[#0C0C0C] p-7">
                <div className="flex gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} />
                  ))}
                </div>
                <p className="mt-5 flex-1 text-[15px] leading-relaxed text-gray-300">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-7 flex items-center gap-3">
                  <span
                    className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold text-white"
                    style={{ background: t.color }}
                  >
                    {t.name.split(" ").map((n) => n[0]).join("")}
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-white">{t.name}</div>
                    <div className="text-xs text-gray-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================= FINAL CTA ============================= */}
      <section
        className="px-4 sm:px-6 lg:px-8 py-24"
        style={{
          background:
            "radial-gradient(100% 90% at 50% 100%, rgba(23,189,59,0.16) 0%, rgba(23,189,59,0.03) 40%, rgba(5,5,5,0) 65%), #050505",
        }}
      >
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="font-display text-[44px] leading-[1.05] sm:text-[68px]">
            Your next event <span style={{ color: GREEN }}>deserves better.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-[560px] text-lg text-gray-400">
            Join 1,200+ organisers who&apos;ve moved to Fusshn. No commission, no complexity &mdash; just events that
            sell.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {finalPills.map((p) => (
              <span
                key={p}
                className="rounded-full border border-[#262626] bg-[#0E0E0E] px-5 py-2.5 text-sm text-gray-300"
              >
                {p}
              </span>
            ))}
          </div>

          <div className="mt-12 flex flex-col items-center gap-5">
            <a
              href="https://organiser.fusshn.com"
              className="group inline-flex items-center justify-center gap-2 rounded-xl px-10 py-4 text-lg font-semibold text-white no-underline transition-transform duration-300 hover:scale-[1.03]"
              style={{ background: GREEN, boxShadow: "0 12px 30px rgba(23,189,59,0.35)" }}
            >
              Start listing for free
              <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link
              to="/Contact_Us"
              className="inline-flex items-center gap-1.5 text-sm text-gray-400 no-underline transition-colors hover:text-white"
            >
              Or talk to a Fusshn team member
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SetupAccount;
